import { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiAccountOutline, mdiArrowRight, mdiPhone, mdiShieldCheck } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";
import * as Yup from "yup";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import CustomMap from "components/CustomMap";
import Header from "components/Header";
import Input from "components/Input";
import Modal from "components/Modal";
import Rater from "components/Rater";
import Select from "components/Select";
import { ArrowDotRightBigIcon } from "components/SVGIcons";
import Textarea from "components/Textarea";
import Tooltip from "components/Tooltip";
import { LOAD_STATUS_LIST } from "constants/loads";
import { routes } from "constants/routes";
import { EQUIPMENT_TYPES } from "constants/signup";
import {
  convertDiffToAge,
  convertLoadData,
  convertToFullAddress,
  parseError
} from "helpers/convertFormData";
import { formatNumber } from "helpers/formatNumber";
import { loadInfo } from "helpers/load";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useColorScheme } from "hooks/useColorScheme";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import {
  ICreateBidForm,
  ICreateBidPerMileForm,
  ILoadBid,
  ILoadQuestion,
  IQueryLoadDetailData
} from "models/Load";
import { IOtherUserProfile } from "models/User";
import { getAccountAPI } from "services/api/user";
import { postBlockUsersAPI } from "services/api/userSetting";
import { AppDispatch } from "store";
import { selectAccount } from "store/auth/selector";
import { handleLoading } from "store/global/loader";
import LoadQuestion from "../../modules/LoadQuestion";
import {
  MUTATION_CREATE_BID,
  MUTATION_CREATE_LOAD_QUESTION,
  MUTATION_DELETE_BID,
  MUTATION_SAVED_LOAD,
  QUERY_LOAD_DETAIL,
  QUERY_LOAD_QUESTION
} from "../query";
import TimeLeftToBid from "./TimeLeftToBid";
import TimeLeftToBidMobile from "./TimeLeftToBidMobile";
import LoadDetailSkeleton from "../LoadDetailSkeleton";
import styles from "./style.module.scss";
import DollarInput from "components/DollarInput";
import { useAnalytics } from "hooks/useAnalytics";

const createBidFormSchema = Yup.object<Partial<ICreateBidForm>>({
  amount: Yup.string().required("This field is required.")
});

const createBidPerMileFormSchema = Yup.object<Partial<ICreateBidPerMileForm>>({
  routeMileage: Yup.string().required("This field is required."),
  ratePerMile: Yup.string().required("This field is required.")
});

export default function SearchLoadDetail() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentIsDark } = useColorScheme();
  const { isDesktop } = useDeviceType();
  const { loadId } = useParams();
  const [company, setCompany] = useState<IOtherUserProfile>();
  const [isCompanyLoading, setIsCompanyLoading] = useState(true);
  const [newQuestion, setNewQuestion] = useState("");
  const [bidList, setBidList] = useState<ILoadBid[]>([]);
  const [retractData, setRetractData] = useState<{
    bidId: string;
    amount: number;
  }>({
    bidId: "",
    amount: 0
  });
  const [totalBidAmount, setTotalBidAmount] = useState(0);
  const { trackAllEvents } = useAnalytics();
  const { userPermission, account } = useSelector(selectAccount);

  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();
  const [isRetractConfirmModalOpen, toggleRetractConfirmModal] = useToggle();
  const [isBiddingModalOpen, toggleBiddingModal] = useToggle();
  const [isSubscribeModalOpen, toggleSubscribeModal] = useToggle();

  //graphQL
  const { data, error } = useQuery(QUERY_LOAD_DETAIL, {
    variables: { id: loadId },
    fetchPolicy: "cache-and-network"
  });

  const {
    data: loadQuestionData,
    error: loadQuestionError,
    refetch
  } = useQuery(QUERY_LOAD_QUESTION, {
    variables: { loadId: loadId },
    fetchPolicy: "cache-and-network"
  });

  const loadQuestion: ILoadQuestion[] = useMemo(() => {
    if (loadQuestionData?.loadQuestions) {
      return loadQuestionData.loadQuestions.data ?? [];
    }
    return [];
  }, [loadQuestionData]);

  useEffect(() => {
    if (error) {
      showErrorMessage(error.message);
    }
    if (loadQuestionError) {
      showErrorMessage(loadQuestionError.message);
    }
  }, [error, loadQuestionError]);

  const [saveLoad] = useMutation(MUTATION_SAVED_LOAD, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [CreateBid] = useMutation(MUTATION_CREATE_BID, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [DeleteBid] = useMutation(MUTATION_DELETE_BID, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [CreateQuestion] = useMutation(MUTATION_CREATE_LOAD_QUESTION, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const closeTime = useMemo(() => {
    if (!data) return 0;
    return dayjs(data.load.timeEnd).diff(dayjs());
  }, [data]);

  const loadData = useMemo(() => {
    if (!data) return null;
    setBidList(data.load.bids.filter((bid: any) => bid.bidStatus !== "retracted"));
    return convertLoadData(data.load) as IQueryLoadDetailData;
  }, [data]);

  useEffect(() => {
    const fetchCompanyName = async () => {
      if (!data?.load?.user?.id) {
        setIsCompanyLoading(false);
        return;
      }
      setIsCompanyLoading(true);
      await getAccountAPI(data?.load?.user?.id)
        .then((res) => {
          if (res.data) {
            setCompany(res.data);
          }
        })
        .finally(() => setIsCompanyLoading(false));
    };
    fetchCompanyName();
  }, [data, data?.load]);
  const createBidMethods = useForm<Partial<ICreateBidForm>>({
    resolver: yupResolver(createBidFormSchema),
    values: {
      status: LOAD_STATUS_LIST.find((data) => data.status === loadData?.status)
    }
  });
  const {
    register,
    control,
    formState: { errors, isSubmitting },
    reset: resetBidForm,
    handleSubmit
  } = createBidMethods;
  const methods = useForm<Partial<ICreateBidPerMileForm>>({
    resolver: yupResolver(createBidPerMileFormSchema)
  });
  const {
    register: register2,
    formState: { errors: submitBidErrors, isSubmitting: isSubmittingBid },
    getValues,
    setValue: setBidValue,
    reset: resetBidPerMileForm,
    handleSubmit: handleSubmit2
  } = methods;

  if (!loadData || isCompanyLoading) return <LoadDetailSkeleton />;

  const {
    id,
    timePosted,
    originAddress,
    originCity,
    originState,
    destinationAddress,
    destinationCity,
    destinationState,
    trailerType,
    targetRate,
    length,
    weight,
    timeDelivery,
    timeStart: { startDay, startTime },
    timeEnd: { endDay, endTime },
    user,
    originCoords,
    destinationCoords,
    details,
    linehaulRate,
    linehaulDistance,
    assignedUser
  } = loadData;

  const LoadAge = convertDiffToAge(dayjs().diff(dayjs(timePosted), "milliseconds"));
  const address = [user.address, user.city, user.state, user.country]
    .filter((item) => !!item && item !== "undefined")
    .join(", ");

  const coords = [
    ...(originCoords ? [{ lat: originCoords.latitude, lng: originCoords.longitude }] : []),
    ...(destinationCoords
      ? [{ lat: destinationCoords.latitude, lng: destinationCoords.longitude }]
      : [])
  ];

  const centerCoords = originCoords
    ? { lat: originCoords.latitude, lng: originCoords.longitude }
    : { lat: 0, lng: 0 };

  //go to profile
  function goProfile() {
    navigate(`${routes.USER}/${user?.id}`);
  }

  const goBilling = () => {
    navigate(routes.SETTINGS_BILLING);
  };

  const createUserSavedLoad = async () => {
    try {
      await saveLoad({
        variables: {
          loadId: loadId
        }
      });
      showSuccessMessage("Successfully saved load.");
      navigate(routes.TRUCKS);
    } catch (error) {
      showErrorMessage("Failed to save load.");
    }
  };

  const sendLoadQuestion = async () => {
    if (userPermission.bid_on_shipper_loads) {
      try {
        dispatch(handleLoading(true));
        await CreateQuestion({
          variables: {
            loadId: loadId,
            question: newQuestion
          }
        });
        await refetch();
      } catch (error) {
        showErrorMessage("Failed to send question.");
      } finally {
        dispatch(handleLoading(false));
      }
      setNewQuestion("");
    } else {
      toggleSubscribeModal();
    }
  };

  const viewLaneRateTrends = () => {
    if (userPermission.bid_on_shipper_loads) {
      navigate(location.pathname + "/lane-trends", {
        state: {
          origin_city: originCity,
          origin_state: originState,
          destination_city: destinationCity,
          destination_state: destinationState
        }
      });
    } else {
      toggleSubscribeModal();
    }
  };

  const onSubmit = async (formData: Partial<ICreateBidForm>) => {
    if (userPermission.bid_on_shipper_loads) {
      dispatch(handleLoading(true));
      try {
        const { data: createBidData } = await CreateBid({
          variables: {
            loadId: id,
            amount: Number(formData.amount),
            bidderNotes: formData.note
          }
        });
        if (createBidData.createBid.bid) {
          showSuccessMessage("Bid created successfully");
          resetBidForm({
            amount: "" as any,
            note: ""
          });
          setBidList((prev) => [...prev, createBidData.createBid.bid]);
        } else {
          showErrorMessage("Failed to create bid");
        }
      } finally {
        dispatch(handleLoading(false));
      }
    } else {
      toggleSubscribeModal();
    }
  };

  const onBiddingSubmit = async (formData: Partial<ICreateBidPerMileForm>) => {
    try {
      dispatch(handleLoading(true));
      const { data: createBidData } = await CreateBid({
        variables: {
          loadId: id,
          amount: Number(formData.ratePerMile! * formData.routeMileage!),
          bidderNotes: formData.note
        }
      });
      if (createBidData.createBid.bid) {
        showSuccessMessage("Bid created successfully");
        resetBidPerMileForm();
        setBidList((prev) => [...prev, createBidData.createBid.bid]);
      } else {
        showErrorMessage("Failed to create bid");
      }
    } finally {
      dispatch(handleLoading(false));
      toggleBiddingModal();
    }
  };

  const retractBid = async (bidId: string) => {
    try {
      dispatch(handleLoading(true));
      const { data } = await DeleteBid({
        variables: {
          bidId: bidId
        }
      });
      if (data?.deleteBid.success) {
        const selectedBidList = bidList.filter((item) => item.id !== bidId);
        setBidList(selectedBidList);
        toggleRetractConfirmModal();
        await refetch();
        setRetractData({
          bidId: "",
          amount: 0
        });
      }
    } finally {
      dispatch(handleLoading(false));
    }
  };

  const blockLoad = async () => {
    if (account.id !== company?.id) {
      dispatch(handleLoading(true));
      await postBlockUsersAPI(user.id)
        .then(() => {
          showSuccessMessage("Successfully blocked user.");
          toggleConfirmModal();
          navigate(routes.TRUCKS);
        })
        .catch(() => {
          showErrorMessage("Failed to block user.");
        })
        .finally(() => dispatch(handleLoading(false)));
    } else {
      showErrorMessage("You can't block yourself.");
    }
  };

  const goToUserProfile = (id: string) => {
    navigate(routes.USER + `/${id}`);
  };

  return (
    <MainLayout
      desktopTitle="Load Search"
      className="!pr-14 !pl-16 mobile:!px-5"
      customHeader={<Header label="Load Details" />}>
      <FormProvider {...createBidMethods}>
        <form className={styles.loadDetailBody} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.companyName}>
            <span className="text-xl font-medium">Company Name: {company?.dba}</span>
            <Button type="button" onClick={createUserSavedLoad}>
              Save this Load
            </Button>
          </div>
          <div className={styles.loadDetailHeader}>
            <div className={styles.loadDetailHeaderCol}>
              <div>
                <span className="mobile:hidden desktop:font-normal">Origin: </span>
                {convertToFullAddress(originAddress, originCity, originState, "")}
              </div>
              <ArrowDotRightBigIcon className="flex-none w-fit" />
              <div>
                <span className="mobile:hidden desktop:font-normal">Destination: </span>
                {convertToFullAddress(destinationAddress, destinationCity, destinationState, "")}
              </div>
            </div>

            <h4 className="text-sm font-semibold desktop:hidden">
              {loadInfo(trailerType, length, weight)}
            </h4>
          </div>

          <ul
            className={clsx(
              styles.loadDetailCard,
              !currentIsDark && "drop-shadow-[0_2px_4.5px_#9E9E9E33]",
              isDesktop ? "grid-cols-7" : "grid-cols-3"
            )}>
            <li>
              <span className="text-xs text-placeholder">Type of Truck</span>
              <span className="capitalize">
                {trailerType !== ""
                  ? EQUIPMENT_TYPES.find(({ value }) => value === trailerType)?.subLabel
                  : "Any"}
              </span>
            </li>
            {isDesktop ? (
              <>
                <li>
                  <span className="text-xs text-placeholder">Weight</span>
                  <span>{weight} lbs</span>
                </li>
                <li>
                  <span className="text-xs text-placeholder">Length</span>
                  <span>{length} ft</span>
                </li>
              </>
            ) : (
              <li>
                <span className="text-xs text-placeholder">Distance</span>
                <span>{`${linehaulDistance} mi`}</span>
              </li>
            )}
            <li>
              <span className="text-xs text-placeholder">Load Age</span>
              <span>{LoadAge}</span>
            </li>
            <li>
              <span className="text-xs text-placeholder">Ready</span>
              <span>{startDay ?? ""}</span>
              <span className="text-xs">{startTime ?? ""}</span>
            </li>
            <li>
              <span className="text-xs text-placeholder">Close</span>
              <span>{endDay ?? ""}</span>
              <span className="text-xs">{endTime ?? ""}</span>
            </li>
            <li>
              <span className="text-xs text-placeholder">Delivers</span>
              <span>{timeDelivery?.deliveryDay !== "" ? timeDelivery?.deliveryDay : "NaN"}</span>
              <span className="text-xs">{timeDelivery?.deliveryTime ?? "NaN"}</span>
            </li>
          </ul>

          <div className={styles.loadDetailBoxGroup}>
            <div className={styles.loadDetailBoxCard1}>
              <div className={styles.loadDetailCardTitle}>
                <span className="desktop:hidden">Company</span>
                <span className="text-lg mobile:hidden">Company Information</span>
              </div>

              <div className={styles.loadDetailCardTop}>
                <div className={styles.companyInfo}>
                  <span>{user.mc && `MC#: ${user.mc}`}</span>
                  <span>{user.dot && `DOT#: ${user.dot}`}</span>
                  <span>Name: {company?.dba}</span>
                  <span>Address: {address}</span>
                </div>

                <div className={styles.loadDetailCardTopText}>
                  <span className="font-bold capitalize">{company?.dba}</span>
                  <button
                    type="button"
                    className={styles.loadDetailCardTopIcon}
                    onClick={goProfile}>
                    <Icon path={mdiAccountOutline} className="m-auto text-placeholder" />
                  </button>
                </div>

                <div className={styles.loadDetailCardMedium}>
                  <span>Block:</span>
                  <button type="button" className="underline" onClick={goProfile}>
                    View Profile
                  </button>
                </div>

                <div className={styles.loadDetailCardBottom}>
                  <span className="text-base mobile:hidden">Block</span>
                  <Button
                    type="button"
                    variant="outline"
                    className="desktop:!h-11 !h-7 mobile:!rounded-full"
                    onClick={toggleConfirmModal}>
                    User
                  </Button>
                </div>
              </div>
            </div>

            <div className={clsx(styles.loadDetailBoxCard, "desktop:!hidden")}>
              <div className={styles.loadDetailCardTitle}>
                <span>Company Information</span>
              </div>

              <div className={styles.loadDetailCardContent}>
                <span>{user.mc && `MC#: ${user.mc}`}</span>
                <span>{user.dot && `DOT#: ${user.dot}`}</span>
                <span>{details}</span>
                <span>{address}</span>
              </div>
            </div>

            <div className={styles.loadDetailBoxCard}>
              <div className={styles.loadDetailCardTitle}>
                <span className="desktop:hidden">Contact or Bid</span>
                <span className="mobile:hidden desktop:text-lg">Contact</span>
              </div>

              <div className={styles.loadDetailCardContent}>
                <span>Contact : {user?.contactName}</span>
                <div className="flex">
                  <span>Phone :&nbsp;</span>
                  <a href={`tel:+${user.contactPhone}`} className="underline text-info">
                    {user.contactPhone}
                  </a>
                </div>

                <div className="break-all">
                  <span className="flex">Email :&nbsp;</span>
                  <a href={`mailto:${user.email}`} className="underline text-info">
                    {user.email}
                  </a>
                </div>

                <div className={styles.loadDetailCardContentBottom}>
                  <a href={`tel:+${user.contactPhone}`} className={styles.callButton}>
                    <Icon path={mdiPhone} className="m-auto" />
                  </a>

                  <a
                    href={`mailto:${user.email}`}
                    className={clsx(styles.loadDetailCardContentButton, "mobile:!rounded-full")}>
                    Email
                  </a>
                </div>
              </div>
            </div>

            <div className={styles.loadDetailBoxCard}>
              <div className={styles.loadDetailCardTitle}>
                <span className="desktop:text-lg">Reporting</span>
              </div>

              <div className={clsx(styles.loadDetailCardContent, "desktop:mt-3")}>
                <div className={styles.loadDetailCardContentTop}>
                  <button type="button" className="text-info" onClick={goProfile}>
                    Rate This User
                  </button>

                  <Link
                    to="https://safer.fmcsa.dot.gov/query.asp"
                    target="_blank"
                    className="desktop:hidden text-info">
                    FMCSA Report
                  </Link>
                </div>

                <Rater value={company?.average_rating ? company.average_rating / 20 : 0} readOnly />

                {company?.has_verified_email ? (
                  <Tooltip
                    position="left"
                    message="This carrier has been verified for liability insurance, business legitimacy and authority compliance. We still recommend verifying cargo insurance and other requirements with the carrier as needed.">
                    <div className={styles.tooltip}>
                      <Icon path={mdiShieldCheck} className="text-primary size-11" />
                      <span>Haul Pay Verified Carrier</span>
                    </div>
                  </Tooltip>
                ) : null}
              </div>
            </div>

            <div className={styles.loadDetailBoxCard2}>
              <div className={styles.targetRateBox}>
                <span>{`Target Rate: ${formatNumber(targetRate / 100, 2)}`}</span>
                {assignedUser?.id ? (
                  <span className="text-error">Load Has Been Covered</span>
                ) : (
                  <TimeLeftToBidMobile closeTime={closeTime} />
                )}
              </div>
              <div className={styles.statusBox}>
                <span className="text-base mobile:hidden">Status</span>
                <Controller
                  name="status"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Select options={LOAD_STATUS_LIST} value={value} onChange={onChange} disabled />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="my-4">
            <h4 className="font-medium">Notes:</h4>
            <span className="text-xs">No details specified</span>
          </div>

          <div className={styles.loadDetailAlarm}>
            <div className={styles.loadDetailRate}>
              <div className={styles.loadDetailRateTitle}>
                <span>Target Rate</span>
              </div>

              <div className={styles.loadDetailRateContent}>
                <span>{`${formatNumber(targetRate / 100, 2)}`}</span>
              </div>
            </div>

            <div className={styles.loadDetailAlarmBid}>
              {assignedUser?.id ? (
                <span className="text-error">Load Has Been Covered</span>
              ) : (
                <TimeLeftToBid closeTime={closeTime} />
              )}
            </div>
          </div>

          <div className="flex justify-end mt-4 desktop:hidden">
            <Button type="button" onClick={createUserSavedLoad}>
              Save this Load
            </Button>
          </div>

          <div className={styles.loadDetailBidding}>
            <h3 className="text-xl font-medium">Bidding</h3>

            <div className={styles.loadDetailBiddingBody}>
              <div className={styles.loadDetailBiddingBodyTitle}>
                <p className={styles.loadDetailBiddingBodyLeft}>Amount</p>
                <span className="py-3 pl-7">Time Submitted</span>
              </div>

              {bidList.length ? (
                <div className={styles.loadDetailBiddingBodyContent}>
                  {bidList.map((item) => (
                    <>
                      <div key={item.id} className={styles.loadDetailBiddingBodyCol}>
                        <span>{formatNumber(item.amount, 2)}</span>
                        <span>{dayjs(item.modified).format("MM/DD/YYYY hh:mm A")}</span>
                      </div>

                      <div className={styles.loadDetailBiddingBodyButton}>
                        {!item.acceptedBy?.id ? (
                          <Button
                            type="button"
                            onClick={() => {
                              setRetractData({
                                bidId: item.id,
                                amount: item.amount
                              });
                              toggleRetractConfirmModal();
                            }}>
                            Retract
                          </Button>
                        ) : (
                          <div className="flex flex-col items-end">
                            <span className="text-sm text-success">You Won This Bid</span>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => goToUserProfile(item.acceptedBy.id)}>
                              View Contact Info
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  ))}
                </div>
              ) : null}
            </div>

            {assignedUser?.id || closeTime < 0 ? null : (
              <>
                <div className={styles.loadDetailBiddingInputs}>
                  <div className={styles.loadDetailBiddingInput}>
                    <DollarInput
                      name="amount"
                      error={errors.amount}
                      placeholder="Enter bid (Numbers only)"
                      className="flex-1"
                    />
                    <Button
                      type="submit"
                      className={clsx(styles.loadDetailBiddingSubmit, "disabled:!bg-subtitle")}
                      loading={isSubmitting}>
                      Submit
                    </Button>
                  </div>
                  <Input
                    name="note"
                    register={register}
                    placeholder="Include a note with bid (optional)"
                  />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className={clsx(styles.loadDetailBiddingEnter, "disabled:!bg-subtitle")}
                  onClick={() => {
                    trackAllEvents("loads_search_enterpermile");
                    userPermission.bid_on_shipper_loads
                      ? toggleBiddingModal()
                      : toggleSubscribeModal();
                  }}>
                  Enter Per Mile
                </Button>
              </>
            )}
          </div>

          <div className={styles.loadDetailAsk}>
            <h4 className="text-xl font-medium">Ask Question</h4>

            {loadQuestion.length ? (
              <LoadQuestion goToUserProfile={goToUserProfile} loadQuestion={loadQuestion} />
            ) : (
              <span className="text-sm text-placeholder">No questions posted yet.</span>
            )}
          </div>

          <div className={styles.loadDetailSend}>
            <Textarea
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              className="flex-1"
              placeholder="Type your question here..."
            />
            <Button
              type="button"
              className={clsx(styles.loadDetailSendButton, newQuestion ? "" : "!bg-subtitle")}
              onClick={sendLoadQuestion}
              disabled={!newQuestion}>
              Send
            </Button>
          </div>

          <div className="flex flex-col gap-3">
            <div className={styles.labeledItem}>
              <span className={styles.label}>Market Average:</span>
              <div className={styles.content}>
                {linehaulRate ?? "Based on truckload bid trend for this lane."}
              </div>
            </div>

            {isDesktop ? (
              <Button
                variant="outline"
                className="py-4 my-4 mobile:justify-between desktop:my-2 desktop:w-56"
                onClick={() => {
                  trackAllEvents("loads_search_viewlaneratetrend");
                  viewLaneRateTrends();
                }}>
                <span className="mobile:text-link">View Lane Rate Trend</span>
                <Icon path={mdiArrowRight} className="desktop:hidden" />
              </Button>
            ) : (
              <Button
                variant="outline"
                className="justify-between py-4 my-4 disabled:!cursor-not-allowed"
                onClick={() => {
                  trackAllEvents("loads_search_viewlaneratetrend");
                  viewLaneRateTrends();
                }}>
                <span className="text-link">View Lane Rate Trend</span>
                <Icon path={mdiArrowRight} />
              </Button>
            )}
          </div>

          <div className={styles.loadDetailMap}>
            <h4 className="font-medium">Map:</h4>

            <CustomMap points={coords} center={centerCoords} isDirections />
          </div>

          <div className={styles.loadDetailDirection}>
            <h4 className="font-medium">Directions:</h4>

            <p className="text-xs leading-5">
              Please note that directions may be different from truck route specific GPS directions.
            </p>

            <a
              href={`https://www.google.com/maps/dir/?api=1&origin=${originCoords?.latitude},${originCoords?.longitude}&destination=${destinationCoords?.latitude},${destinationCoords?.longitude}&travelmode=driving`}
              target="_blank"
              rel="noreferrer"
              className={styles.loadDetailDirectionLink}>
              <span className="flex text-lg underline">Open Directions</span>
              <Icon path={mdiArrowRight} size="24px" />
            </a>
          </div>

          <div className={styles.loadDetailReturn}>
            <h3 className="font-medium">Return Loads:</h3>
            <p className="text-xs font-light">Nothing here yet.</p>
          </div>
        </form>
      </FormProvider>
      <ConfirmationModal
        isConfirmModalOpen={isRetractConfirmModalOpen}
        toggleConfirmModal={toggleRetractConfirmModal}
        onOK={() => {
          trackAllEvents("loads_search_retract");
          retractBid(retractData.bidId);
        }}
        onCancel={() => {
          toggleRetractConfirmModal();
          setRetractData({
            bidId: "",
            amount: 0
          });
        }}
        labelOKButton="Retract"
        content={
          `Are you sure you want to retract bid of ` + formatNumber(retractData.amount, 2) + `?`
        }
      />

      <ConfirmationModal
        isConfirmModalOpen={isConfirmModalOpen}
        toggleConfirmModal={toggleConfirmModal}
        onOK={blockLoad}
        content="Are you sure you want to block all posts by this user? You can manage blocked users from Settings under the Account section."
      />

      <Modal
        isOpen={isBiddingModalOpen}
        closeModal={() => {
          toggleBiddingModal();
          resetBidPerMileForm();
        }}
        wrapperClass="!my-auto desktop:!min-h-fit"
        dialogClass="!bg-foreground !opacity-95 !px-4 !py-5 gap-7"
        closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
        <h3 className="text-xl font-bold">Per Mile Rate</h3>
        <FormProvider {...methods}>
          <form className="flex flex-col gap-5" onSubmit={handleSubmit2(onBiddingSubmit)}>
            <div className="flex flex-col gap-4.5">
              <Input
                type="number"
                inputMode="decimal"
                step=".01"
                name="routeMileage"
                register={register2}
                label="&nbsp;&nbsp;Route Mileage"
                placeholder="miles"
                error={submitBidErrors.routeMileage}
                onChange={(e) =>
                  setTotalBidAmount(Number(e.target.value) * (getValues("ratePerMile") as number))
                }
                onBlur={(e) => {
                  const value = parseFloat(e.target.value).toFixed(2);
                  setBidValue("routeMileage", parseFloat(value));
                }}
              />
              <DollarInput
                name="ratePerMile"
                error={submitBidErrors.ratePerMile}
                label="&nbsp;&nbsp;Rate Per Mile"
                placeholder="$/mile"
                className="flex-1"
                onChange={(e) => {
                  setTotalBidAmount((getValues("routeMileage") as number) * Number(e.target.value));
                }}
                onBlur={(e) => {
                  const value = parseFloat(e.target.value).toFixed(2);
                  setBidValue("ratePerMile", parseFloat(value));
                }}
              />
              <Input
                name="note"
                register={register2}
                label="&nbsp;&nbsp;Add Note w/ Bid"
                placeholder="Note"
              />
            </div>
            <div className="flex items-center justify-between gap-1">
              <span>{`Total: ${totalBidAmount ? formatNumber(totalBidAmount, 2) : "n/a"}`}</span>
              <div className="flex justify-end gap-2.5">
                <Button
                  variant="outline"
                  type="button"
                  rounded
                  onClick={toggleBiddingModal}
                  className="!h-10 !px-3">
                  Cancel
                </Button>
                <Button type="submit" rounded className="!h-10 !px-3" loading={isSubmittingBid}>
                  Submit bid
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </Modal>
      <ConfirmationModal
        isConfirmModalOpen={isSubscribeModalOpen}
        toggleConfirmModal={toggleSubscribeModal}
        onOK={goBilling}
        title="Warning"
        content="Please subscribe to use this feature."
        labelOKButton="Subcribe"
      />
    </MainLayout>
  );
}
